import { html, css } from 'lit';

import {
  auditEncounterAsync,
  saveAuditResults,
} from '../../../../neb-api-client/src/audit-shield-api-client';
import { OVERLAY_WIDTH_MEDIUM } from '../../../../neb-styles/neb-variables';

import Overlay from './neb-overlay';

class NebOverlayAuditShield extends Overlay {
  static get properties() {
    return {
      __chartNotes: { type: Object },
      __chargeList: { type: Object },
      __diagnosesList: { type: Object },
      __auditStatus: { type: String },
      __auditResult: { type: Object },
      __auditError: { type: Object },
      __progress: { type: Number },
      __dots: { type: Number },
      __encounterId: { type: String },
      __startTime: { type: Number },
      __elapsedTime: { type: Number },
      __elapsedTimer: { type: Number },
    };
  }

  initState() {
    super.initState();
    this.__chartNotes = null;
    this.__chargeList = null;
    this.__diagnosesList = null;
    this.__auditStatus = null;
    this.__auditResult = null;
    this.__auditError = null;
    this.__progress = 0;
    this.__dots = 0;
    this.__dotsTimer = null;
    this.__encounterId = null;
    this.__startTime = null;
    this.__elapsedTime = 0;
    this.__elapsedTimer = null;
  }

  disconnectedCallback() {
    if (super.disconnectedCallback) {
      super.disconnectedCallback();
    }
    this._clearDotsTimer();
    this._clearElapsedTimer();
  }

  _clearDotsTimer() {
    if (this.__dotsTimer) {
      clearInterval(this.__dotsTimer);
      this.__dotsTimer = null;
    }
  }

  _clearElapsedTimer() {
    if (this.__elapsedTimer) {
      clearInterval(this.__elapsedTimer);
      this.__elapsedTimer = null;
    }
  }

  _startDotsAnimation() {
    this._clearDotsTimer();
    this.__dots = 0;
    this.__dotsTimer = setInterval(() => {
      this.__dots = (this.__dots + 1) % 4;
      this.requestUpdate();
    }, 200);
  }

  _startElapsedTimer() {
    this._clearElapsedTimer();
    this.__startTime = Date.now();
    this.__elapsedTime = 0;
    this.__elapsedTimer = setInterval(() => {
      this.__elapsedTime = Date.now() - this.__startTime;
      this.requestUpdate();
    }, 500); // Update every 500ms
  }

  _getCurrentPhaseIndex() {
    // Each phase should take about 5 seconds (20 seconds total / 4 phases)
    const phasesDuration = 5000; // 5 seconds per phase
    const currentPhase = Math.min(
      Math.floor(this.__elapsedTime / phasesDuration),
      3, // Max phase index is 3 (0-3)
    );
    return currentPhase;
  }

  initHandlers() {
    super.initHandlers();

    this.handlers = {
      ...this.handlers,
    };
  }

  async updated(changedProps) {
    if (changedProps.has('model')) {
      console.log('this.model :>> ', this.model.chartNotes);
      this.__chartNotes = this.model.chartNotes;
      this.__chargeList = this.model.chargeList;
      this.__diagnosesList = this.model.diagnosesList;
      this.__encounterId = this.model.encounterId;
      await this._startAudit();
    }

    // Start or stop fake progress and dots animation based on status
    if (changedProps.has('__auditStatus')) {
      if (this.__auditStatus === 'pending') {
        this._startElapsedTimer();
        this._startDotsAnimation();
      } else if (this.__auditStatus === 'done') {
        this._clearElapsedTimer();
        this._clearDotsTimer();
        this.requestUpdate();
      } else if (this.__auditStatus === 'error') {
        this._clearElapsedTimer();
        this._clearDotsTimer();
      }
    }

    if (super.updated) {
      super.updated(changedProps);
    }
  }

  static get styles() {
    return [
      super.styles,
      css`
        .content {
          width: ${OVERLAY_WIDTH_MEDIUM};
          max-height: 100vh;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          border: 1px solid rgba(255, 255, 255, 0.3);
          animation: slideUp 0.6s ease-out;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .header-container {
          background: linear-gradient(
            135deg,
            #1976d2 0%,
            rgb(157, 180, 240) 100%
          );
          color: white;
          text-align: center;
          position: relative;
          overflow: hidden;
          flex-shrink: 0;
          box-shadow:
            0 0 20px rgba(173, 216, 230, 0.6),
            0 0 40px rgba(173, 216, 230, 0.4),
            0 0 60px rgba(173, 216, 230, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.2);
        }

        .header-container::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.08)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
          opacity: 0.3;
        }

        .main-container {
          padding: 32px;
          box-sizing: border-box;
          flex: 1;
          overflow-y: auto;
          background: linear-gradient(135deg, #fafbff 0%, #f8fafc 100%);
        }

        .heading {
          font-size: 2.5rem;
          font-weight: 700;
          color: white;
          margin-bottom: 16px;
          letter-spacing: 1px;
          text-align: center;
          position: relative;
          z-index: 1;
        }
        neb-loading-spinner {
          margin-right: 8px;
        }
        .progress-label {
          margin-bottom: 4px;
          text-align: right;
          color: #1976d2;
          font-weight: 500;
        }
        .progress-bar-bg {
          width: 100%;
          background: #e3eaf3;
          border-radius: 8px;
          height: 14px;
          box-shadow: 0 2px 8px 0 rgba(25, 118, 210, 0.08);
          overflow: hidden;
          margin-bottom: 16px;
        }
        .progress-bar {
          height: 100%;
          border-radius: 8px;
          background: linear-gradient(90deg, #2196f3 0%, #21cbf3 100%);
          transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .progress-header-box {
          background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
          color: white;
          border: none;
          border-radius: 12px;
          padding: 16px 24px;
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 15px rgba(251, 191, 36, 0.25);
          position: relative;
          z-index: 1;
        }

        .progress-header-box::before {
          content: '⚠️';
          margin-right: 12px;
          font-size: 1.2rem;
        }

        .progress-header {
          font-size: 1.1rem;
          font-weight: 600;
          margin: 0;
          letter-spacing: 0.5px;
        }

        .success-header-box {
          background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
          color: white;
          border: none;
          border-radius: 12px;
          padding: 16px 24px;
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 15px rgba(52, 211, 153, 0.25);
        }

        .success-header {
          font-size: 1.1rem;
          font-weight: 600;
          color: white;
          margin: 0;
          letter-spacing: 0.5px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .success-icon {
          width: 22px;
          height: 22px;
          display: inline-block;
        }

        .warning-header-box {
          background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
          color: white;
          border: none;
          border-radius: 12px;
          padding: 16px 24px;
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 15px rgba(251, 191, 36, 0.25);
        }

        .warning-header {
          font-size: 1.1rem;
          font-weight: 600;
          color: white;
          margin: 0;
          letter-spacing: 0.5px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .warning-icon {
          width: 22px;
          height: 22px;
          display: inline-block;
          fill: white;
        }
        .audit-summary {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 24px;
          margin-bottom: 32px;
        }
        .audit-card {
          background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 24px;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }
        .audit-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
        }
        .audit-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 4px;
          height: 100%;
          background: var(--accent-color, #8b5cf6);
        }
        .audit-card.poor::before {
          --accent-color: #f87171;
        }
        .audit-card.incorrect::before {
          --accent-color: #fb923c;
        }
        .audit-card-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 12px;
        }
        .audit-card-status {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.95rem;
          font-weight: 500;
          margin-bottom: 8px;
        }
        .audit-card-score {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.95rem;
          color: #388e3c;
          font-weight: 500;
        }
        .audit-card-noncompliant {
          color: #dc2626;
          font-weight: 600;
          background: #fef2f2;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.9rem;
        }
        .audit-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
          color: white;
          border-radius: 20px;
          padding: 8px 16px;
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 24px;
          box-shadow: 0 2px 8px rgba(52, 211, 153, 0.15);
        }

        .confidence-label {
          font-weight: 500;
          opacity: 1;
        }

        .confidence-value {
          font-weight: 700;
          margin-left: 4px;
        }

        .confidence-low {
          background: linear-gradient(135deg, #f87171 0%, #ef4444 100%);
          color: white;
          border: none;
        }

        .confidence-medium {
          background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
          color: white;
          border: none;
        }

        .confidence-high {
          background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
          color: white;
          border: none;
        }

        .output-summary {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 28px;
          margin-bottom: 32px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .output-summary-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #374151;
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .output-summary-title::before {
          content: '📋';
          font-size: 1.3rem;
        }
        .output-summary-content {
          font-size: 0.9rem;
          line-height: 1.6;
          color: #37474f;
          background: #fff;
          border: 1px solid #e0e7ef;
          border-radius: 6px;
          padding: 16px;
          max-height: 400px;
          overflow-y: auto;
        }
        .output-summary-content h1,
        .output-summary-content h2,
        .output-summary-content h3 {
          color: #1976d2;
          margin: 16px 0 8px 0;
          font-weight: 600;
          border-bottom: 1px solid #e3f2fd;
          padding-bottom: 4px;
        }
        .output-summary-content h1 {
          font-size: 1.2rem;
        }
        .output-summary-content h2 {
          font-size: 1.1rem;
        }
        .output-summary-content h3 {
          font-size: 1rem;
        }
        .output-summary-content strong {
          font-weight: 600;
          color: #1976d2;
        }
        .output-summary-content ul {
          margin: 8px 0;
          padding-left: 20px;
        }
        .output-summary-content li {
          margin-bottom: 6px;
          line-height: 1.5;
        }
        .output-summary-content p {
          margin: 8px 0;
          line-height: 1.6;
        }
        .output-raw {
          font-size: 0.95rem;
          line-height: 1.6;
          color: #37474f;
          white-space: pre-wrap;
          word-wrap: break-word;
          background: #fff;
          border: 1px solid #e0e7ef;
          border-radius: 6px;
          padding: 16px;
          max-height: 300px;
          overflow-y: auto;
        }
        .output-section {
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid #f0f4f8;
        }
        .output-section:last-child {
          border-bottom: none;
        }
        .output-section-title {
          font-size: 1.05rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 8px;
          padding-bottom: 4px;
          border-bottom: 1px solid #f3f4f6;
        }
        .output-section-list {
          margin: 0;
          padding-left: 20px;
        }
        .output-section-item {
          margin-bottom: 6px;
          line-height: 1.5;
        }
        .output-item-subtitle {
          font-weight: 600;
        }
        .output-subsection {
          margin-bottom: 16px;
          padding: 12px;
          background: #fafbff;
          border-radius: 6px;
          border-left: 3px solid #8b5cf6;
        }
        .output-subsection h3 {
          font-size: 0.95rem;
          font-weight: 600;
          color: #374151;
          margin: 0 0 8px 0;
        }
        .nested-list {
          margin: 6px 0;
          padding-left: 20px;
        }
        .nested-list li {
          margin-bottom: 4px;
          line-height: 1.4;
        }
        .compliant-yes {
          color: #2e7d32;
          font-weight: 600;
        }
        .compliant-no {
          color: #d32f2f;
          font-weight: 600;
        }
        .risk-high {
          color: #d32f2f;
          font-weight: 600;
        }
        .risk-medium {
          color: #f57c00;
          font-weight: 600;
        }
        .risk-low {
          color: #2e7d32;
          font-weight: 600;
        }
        .audit-recommendations {
          background: white;
          border-radius: 16px;
          padding: 28px;
          border: 1px solid #e5e7eb;
          margin-top: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .audit-recommendations-title {
          font-size: 1.4rem;
          font-weight: 700;
          color: #374151;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .audit-recommendations-title::before {
          content: '💡';
          font-size: 1.2rem;
        }

        .audit-recommendations-list {
          margin: 0;
          padding: 0;
          list-style: none;
        }

        .audit-recommendations-list li {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px 0;
          border-bottom: 1px solid #f1f5f9;
          color: #374151;
          line-height: 1.5;
        }

        .audit-recommendations-list li:last-child {
          border-bottom: none;
        }

        .audit-recommendations-list li::before {
          content: '→';
          color: #8b5cf6;
          font-weight: bold;
          margin-top: 2px;
          font-size: 1.1rem;
          flex-shrink: 0;
        }

        /* Special emphasis for Compliance Assessment sections */
        .compliance-emphasis {
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          border: 2px solid #f59e0b;
          border-radius: 16px;
          padding: 32px;
          margin: 24px 0;
          box-shadow: 0 4px 20px rgba(245, 158, 11, 0.15);
          position: relative;
          overflow: hidden;
        }

        .compliance-emphasis::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%);
        }

        .compliance-emphasis .output-section-title,
        .compliance-emphasis .output-subsection h3,
        .compliance-emphasis h3 {
          color: #92400e;
          font-weight: 700;
        }

        .compliance-emphasis .compliant-no {
          background: #fef2f2;
          color: #dc2626;
          padding: 6px 12px;
          border-radius: 12px;
          font-weight: 700;
          font-size: 0.95rem;
        }

        .compliance-emphasis .risk-high {
          background: #fef2f2;
          color: #dc2626;
          padding: 6px 12px;
          border-radius: 12px;
          font-weight: 700;
          font-size: 0.95rem;
        }

        .compliance-assessments-container {
          margin-bottom: 32px;
        }

        .compliance-assessments-container .output-summary-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #92400e;
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          gap: 12px;
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          padding: 16px 24px;
          border-radius: 12px;
          border: 1px solid #f59e0b;
        }

        /* Pending Content Styles */
        .pending-container {
          padding: 16px;
          text-align: center;
        }

        .pending-header {
          margin-bottom: 24px;
        }

        .pending-icon-large {
          font-size: 2.5rem;
          margin-bottom: 8px;
          animation: float 3s ease-in-out infinite;
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-6px);
          }
        }

        .pending-title {
          font-size: 1.4rem;
          font-weight: 700;
          color: #374151;
          margin-bottom: 8px;
          letter-spacing: 0.5px;
        }

        .pending-description {
          font-size: 0.9rem;
          color: #6b7280;
          margin: 0;
          line-height: 1.4;
          max-width: 500px;
          margin: 0 auto;
        }

        .pending-progress-section {
          margin-bottom: 24px;
          padding: 16px;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          border-radius: 12px;
          border: 1px solid #e2e8f0;
        }

        .pending-progress-info {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-bottom: 8px;
        }

        .pending-progress-text {
          font-weight: 600;
          color: #374151;
          font-size: 0.9rem;
        }

        .pending-progress-bar-container {
          width: 100%;
          height: 6px;
          background: #e2e8f0;
          border-radius: 3px;
          overflow: hidden;
          position: relative;
        }

        .pending-progress-bar {
          height: 100%;
          border-radius: 3px;
          transition: background-color 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }

        .pending-progress-bar.indeterminate {
          width: 100%;
          background: linear-gradient(
            90deg,
            transparent 0%,
            currentColor 50%,
            transparent 100%
          );
          background-size: 50% 100%;
          animation: indeterminate-progress 2s linear infinite;
        }

        @keyframes indeterminate-progress {
          0% {
            background-position: -50% 0;
          }
          100% {
            background-position: 150% 0;
          }
        }

        .pending-progress-bar.indeterminate::after {
          display: none;
        }

        .pending-phases {
          margin-bottom: 20px;
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }

        .pending-phase {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px;
          background: white;
          border: 2px solid #f1f5f9;
          border-radius: 10px;
          transition: all 0.3s ease;
          opacity: 0.6;
        }

        .pending-phase.completed {
          opacity: 1;
          border-color: #10b981;
          background: linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%);
        }

        .pending-phase.active {
          opacity: 1;
          border-color: #3b82f6;
          background: linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%);
          transform: scale(1.02);
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
        }

        .pending-phase-icon {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          color: white;
          transition: all 0.3s ease;
          flex-shrink: 0;
        }

        .pending-phase-content {
          flex: 1;
          text-align: left;
        }

        .pending-phase-title {
          font-weight: 600;
          color: #374151;
          font-size: 0.85rem;
          margin-bottom: 2px;
        }

        .pending-phase-status {
          font-size: 0.75rem;
          color: #6b7280;
          font-weight: 500;
        }

        .pending-phase.completed .pending-phase-status {
          color: #10b981;
          font-weight: 600;
        }

        .pending-phase.active .pending-phase-status {
          color: #3b82f6;
          font-weight: 600;
        }

        .pending-info-cards {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-bottom: 20px;
        }

        .pending-info-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px;
          background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          transition: transform 0.2s ease;
        }

        .pending-info-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        .pending-info-icon {
          font-size: 1.5rem;
          flex-shrink: 0;
        }

        .pending-info-content {
          flex: 1;
          text-align: left;
        }

        .pending-info-title {
          font-weight: 600;
          color: #374151;
          font-size: 0.85rem;
          margin-bottom: 2px;
        }

        .pending-info-text {
          font-size: 0.75rem;
          color: #6b7280;
          line-height: 1.3;
        }

        .pending-estimated-time {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 12px 20px;
          background: linear-gradient(135deg, #fef7cd 0%, #fef3c7 100%);
          border: 1px solid #f59e0b;
          border-radius: 10px;
          font-weight: 500;
          color: #92400e;
        }

        .pending-time-icon {
          font-size: 1rem;
        }

        .pending-time-text {
          font-size: 0.85rem;
        }
      `,
    ];
  }

  async _startAudit() {
    this.requestUpdate();

    await auditEncounterAsync({
      chartNotes: this.__chartNotes,
      chargeList: this.__chargeList,
      diagnosesList: this.__diagnosesList,
      onStatus: async (status, resultOrError) => {
        this.__auditStatus = status;

        if (status === 'done') {
          const { summary, raw_output: output } = resultOrError;

          this.__auditResult = { summary, output };
          console.log('this.__auditResult :>> ', this.__auditResult);
          this.__auditError = null;
          await saveAuditResults({
            encounterId: this.__encounterId,
            shieldData: this.__auditResult,
          });
        } else if (status === 'error') {
          this.__auditError = resultOrError;
          this.__auditResult = null;
        }
        this.requestUpdate();
      },
    });
  }

  renderContent() {
    return html`
      <div class="header-container">
        <h1 class="heading">Risk Assessment</h1>
      </div>
      <div class="main-container">${this._renderStatusContent()}</div>
    `;
  }

  _renderStatusContent() {
    if (this.__auditStatus === 'pending') {
      return this._renderPendingContent();
    }

    if (this.__auditStatus === 'done') {
      return this._renderDoneContent();
    }

    if (this.__auditStatus === 'error') {
      return this._renderErrorContent();
    }
    return this._renderIdleContent();
  }

  _renderPendingContent() {
    const analysisPhases = [
      {
        icon: '🔍',
        title: 'Analyzing Documentation',
        description:
          'Reviewing chart notes and clinical documentation for completeness and accuracy',
        color: '#3b82f6',
      },
      {
        icon: '💰',
        title: 'Validating Billing Codes',
        description:
          'Cross-referencing CPT codes with documented treatments and diagnoses',
        color: '#8b5cf6',
      },
      {
        icon: '⚖️',
        title: 'Compliance Assessment',
        description:
          'Evaluating adherence to billing regulations and documentation requirements',
        color: '#06b6d4',
      },
      {
        icon: '🛡️',
        title: 'Risk Analysis',
        description:
          'Calculating compliance risk and identifying potential audit flags',
        color: '#10b981',
      },
    ];

    // Calculate which phase we're currently in based on elapsed time
    const currentPhaseIndex = this._getCurrentPhaseIndex();
    const currentPhase = analysisPhases[currentPhaseIndex];

    const dots = '.'.repeat(this.__dots);

    return html`
      <div class="pending-container">
        <div class="pending-header">
          <div class="pending-icon-large">${currentPhase.icon}</div>
          <h2 class="pending-title">${currentPhase.title}${dots}</h2>
          <p class="pending-description">${currentPhase.description}</p>
        </div>

        <div class="pending-progress-section">
          <div class="pending-progress-info">
            <span class="pending-progress-text">Analysis in Progress</span>
          </div>
          <div class="pending-progress-bar-container">
            <div
              class="pending-progress-bar indeterminate"
              style="background-color: ${currentPhase.color};"
            ></div>
          </div>
        </div>

        <div class="pending-phases">
          ${analysisPhases.map((phase, index) => {
            const isCompleted = this._isPhaseCompleted(
              index,
              currentPhaseIndex,
            );
            const isActive = this._isPhaseActive(index, currentPhaseIndex);

            return html`
              <div
                class="pending-phase ${isCompleted
                  ? 'completed'
                  : ''} ${isActive ? 'active' : ''}"
              >
                <div
                  class="pending-phase-icon"
                  style="background-color: ${isCompleted
                    ? phase.color
                    : '#e5e7eb'};"
                >
                  ${phase.icon}
                </div>
                <div class="pending-phase-content">
                  <div class="pending-phase-title">${phase.title}</div>
                  <div class="pending-phase-status">
                    ${this._getPhaseStatus(index, currentPhaseIndex)}
                  </div>
                </div>
              </div>
            `;
          })}
        </div>

        <div class="pending-info-cards">
          <div class="pending-info-card">
            <div class="pending-info-icon">📊</div>
            <div class="pending-info-content">
              <div class="pending-info-title">Comprehensive Analysis</div>
              <div class="pending-info-text">
                AI-powered review of all encounter documentation
              </div>
            </div>
          </div>
          <div class="pending-info-card">
            <div class="pending-info-icon">⚡</div>
            <div class="pending-info-content">
              <div class="pending-info-title">Real-time Processing</div>
              <div class="pending-info-text">
                Advanced algorithms ensure accurate results
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  _isPhaseCompleted(index, currentPhaseIndex) {
    return (
      index < currentPhaseIndex || (index === 3 && this.__elapsedTime >= 20000)
    );
  }

  _isPhaseActive(index, currentPhaseIndex) {
    return (
      index === currentPhaseIndex &&
      !(index === 3 && this.__elapsedTime >= 20000)
    );
  }

  _getPhaseStatus(index, currentPhaseIndex) {
    // Special case: Risk Analysis (index 3) shows as complete after 10 seconds
    if (index === 3 && this.__elapsedTime >= 10000) {
      return '✓ Complete';
    }

    if (index < currentPhaseIndex) {
      return '✓ Complete';
    }

    if (index === currentPhaseIndex) {
      return 'In Progress';
    }
    return 'Pending';
  }

  _renderDoneContent() {
    const result = this.__auditResult || {};
    const isNonCompliant =
      result.summary?.overall_assessment?.billing_accuracy === 'incorrect' ||
      result.overall_assessment?.billing_accuracy === 'incorrect' ||
      this._hasNonCompliantBillingCodes(
        result.summary?.billing_code_analysis || result.billing_code_analysis,
      );
    return html`<div>
      ${isNonCompliant
        ? html`<div class="warning-header-box">
            <h2 class="warning-header">
              <neb-icon icon="neb:warning" class="warning-icon"></neb-icon>
              Audit complete with warnings
            </h2>
          </div>`
        : html`<div class="success-header-box">
            <h2 class="success-header">
              <neb-icon icon="neb:check" class="success-icon"></neb-icon>
              Audit complete!
            </h2>
          </div>`}
      <div class="audit-summary">
        ${this._renderDocumentationQualityCard(result)}
        ${this._renderBillingAccuracyCard(result)}
      </div>
      ${this._renderConfidenceBadge(result)}
      ${this._renderComplianceAssessments(result)}
      ${this._renderOutputSummary(result)}
      ${this._renderRecommendations(result)}
    </div>`;
  }

  _hasNonCompliantBillingCodes(billingCodeAnalysis) {
    if (!billingCodeAnalysis) return false;
    return Object.values(billingCodeAnalysis).some(
      code => code.compliance_assessment?.is_compliant === false,
    );
  }

  _renderDocumentationQualityCard(result) {
    const overallAssessment =
      result.summary?.overall_assessment || result.overall_assessment;
    const quality = overallAssessment?.documentation_quality?.toLowerCase();
    const cardClass = quality === 'poor' ? 'poor' : '';

    return html`<div class="audit-card ${cardClass}">
      <div class="audit-card-title">Documentation Quality</div>
      <div class="audit-card-status">
        <span class="status-label">Status:</span>
        <span class="status-value"
          >${this._getDocumentationQualityDisplay(
            overallAssessment?.documentation_quality,
          )}</span
        >
      </div>
      <div class="audit-card-score">
        <span class="status-label">Assessment:</span>
        <span class="status-value">
          ${this._getDocumentationQualityDisplay(
            overallAssessment?.documentation_quality,
          )}
        </span>
      </div>
    </div>`;
  }

  _getDocumentationQualityDisplay(quality) {
    if (!quality) return '-';
    return quality.charAt(0).toUpperCase() + quality.slice(1);
  }

  _renderBillingAccuracyCard(result) {
    const overallAssessment =
      result.summary?.overall_assessment || result.overall_assessment;
    const isNonCompliant = this._isBillingNonCompliant(
      result,
      overallAssessment,
    );
    const cardClass = isNonCompliant ? 'incorrect' : '';

    return html`<div class="audit-card ${cardClass}">
      <div class="audit-card-title">Billing Accuracy</div>
      <div class="audit-card-status">
        <span class="status-label">Status:</span>
        <span
          class="status-value ${isNonCompliant
            ? 'audit-card-noncompliant'
            : ''}"
          >${overallAssessment?.billing_accuracy || '-'}</span
        >
      </div>
      <div class="audit-card-score">
        <span class="status-label">Compliance:</span>
        <span
          class="status-value ${isNonCompliant
            ? 'audit-card-noncompliant'
            : ''}"
        >
          ${isNonCompliant ? 'Non-compliant' : 'Compliant'}
        </span>
      </div>
    </div>`;
  }

  _isBillingNonCompliant(result, overallAssessment) {
    const isIncorrect = overallAssessment?.billing_accuracy === 'incorrect';
    const hasNonCompliantCodes = this._hasNonCompliantBillingCodes(
      result.summary?.billing_code_analysis || result.billing_code_analysis,
    );
    return isIncorrect || hasNonCompliantCodes;
  }

  _renderConfidenceBadge(result) {
    const confidenceLevel =
      result.summary?.confidence_level?.toLowerCase() || '';

    // If no confidence level is available, don't render the badge
    if (!result.summary?.confidence_level) {
      return '';
    }

    const confidenceClass = this._getConfidenceClass(confidenceLevel);

    return html`<div class="audit-badge ${confidenceClass}">
      <span class="confidence-label">Confidence Level:</span>
      <span class="confidence-value">
        ${result.summary?.confidence_level || '-'}
      </span>
    </div>`;
  }

  _getConfidenceClass(level) {
    switch (level) {
      case 'low':
        return 'confidence-low';
      case 'medium':
        return 'confidence-medium';
      case 'high':
        return 'confidence-high';
      default:
        return '';
    }
  }

  _renderComplianceAssessments(result) {
    const billingCodeAnalysis =
      result.summary?.billing_code_analysis || result.billing_code_analysis;

    if (!billingCodeAnalysis) {
      return '';
    }

    // Extract compliance assessments from billing codes
    const complianceAssessments = [];
    Object.entries(billingCodeAnalysis).forEach(([code, analysis]) => {
      if (analysis.compliance_assessment) {
        complianceAssessments.push({
          code,
          assessment: analysis.compliance_assessment,
        });
      }
    });

    if (complianceAssessments.length === 0) {
      return '';
    }

    return html`
      <div class="compliance-assessments-container">
        <div class="output-summary-title">🛡️ Compliance Assessments</div>
        ${complianceAssessments.map(
          ({ code, assessment }) => html`
            <div class="compliance-emphasis">
              <h3>Billing Code ${code} - Compliance Assessment</h3>
              <ul class="output-section-list">
                <li>
                  <strong>Is Compliant:</strong>
                  <span
                    class="${assessment.is_compliant
                      ? 'compliant-yes'
                      : 'compliant-no'}"
                  >
                    ${assessment.is_compliant ? 'Yes' : 'No'}
                  </span>
                </li>
                <li>
                  <strong>Compliance Risk:</strong>
                  <span
                    class="risk-${assessment.compliance_risk?.toLowerCase()}"
                  >
                    ${assessment.compliance_risk}
                  </span>
                </li>
                ${assessment.primary_issues?.length
                  ? html`
                      <li>
                        <strong>Primary Issues:</strong>
                        <ul class="nested-list">
                          ${assessment.primary_issues.map(
                            issue => html`<li>${issue}</li>`,
                          )}
                        </ul>
                      </li>
                    `
                  : ''}
                ${assessment.recommended_action
                  ? html`
                      <li>
                        <strong>Recommended Action:</strong>
                        ${assessment.recommended_action}
                      </li>
                    `
                  : ''}
              </ul>
            </div>
          `,
        )}
      </div>
    `;
  }

  _renderOutputSummary(result) {
    if (!result.output) {
      return '';
    }

    console.log('result :>> ', result);

    // Try to parse the output as JSON first
    let parsedOutput;

    try {
      parsedOutput =
        typeof result.output === 'string'
          ? JSON.parse(result.output)
          : result.output;
    } catch (e) {
      // If it's not JSON, try to parse as structured sections, then fall back to markdown
      const parsedSections = this._parseOutputSections(result.output);
      console.log('parsedSections :>> ', parsedSections);

      return html` <div class="output-summary-content">
        ${parsedSections.length > 0
          ? parsedSections.map(section => this._renderOutputSection(section))
          : html`${this._parseMarkdown(result.output)}`}
        <
      </div>`;
    }

    // If we successfully parsed JSON, render it in a structured way
    return html` <div class="output-summary-content">
      ${this._renderJsonOutput(parsedOutput)}
    </div>`;
  }

  _parseOutputSections(output) {
    const lines = output.split('\n');
    const sections = [];

    let currentSection = null;

    lines.forEach(line => {
      const trimmedLine = line.trim();

      if (this._isHeaderLine(trimmedLine)) {
        currentSection = this._processHeaderLine(
          trimmedLine,
          currentSection,
          sections,
        );
      } else if (this._isBulletWithSubtitle(trimmedLine, currentSection)) {
        this._processBulletWithSubtitle(trimmedLine, currentSection);
      } else if (this._isRegularBullet(trimmedLine, currentSection)) {
        this._processRegularBullet(trimmedLine, currentSection);
      }
    });

    if (currentSection && !this._isRecommendationSection(currentSection)) {
      sections.push(currentSection);
    }

    // Filter out recommendation sections
    return sections.filter(section => !this._isRecommendationSection(section));
  }

  _isHeaderLine(line) {
    return line.match(/^\*\*(.+?):\*\*$/);
  }

  _processHeaderLine(line, currentSection, sections) {
    const headerMatch = line.match(/^\*\*(.+?):\*\*$/);

    if (currentSection) {
      sections.push(currentSection);
    }
    return {
      title: headerMatch[1],
      items: [],
    };
  }

  _isBulletWithSubtitle(line, currentSection) {
    return currentSection && line.startsWith('- **');
  }

  _processBulletWithSubtitle(line, currentSection) {
    const bulletMatch = line.match(/^- \*\*(.+?):\*\*(.*)$/);

    if (bulletMatch) {
      currentSection.items.push({
        subTitle: bulletMatch[1],
        content: bulletMatch[2].trim(),
      });
    }
  }

  _isRegularBullet(line, currentSection) {
    return currentSection && line.startsWith('-');
  }

  _processRegularBullet(line, currentSection) {
    currentSection.items.push({
      content: line.substring(1).trim(),
    });
  }

  _renderOutputSection(section) {
    return html`
      <div class="output-section">
        <div class="output-section-title">${section.title}</div>
        <ul class="output-section-list">
          ${section.items.map(
            item => html`
              <li class="output-section-item">
                ${item.subTitle
                  ? html`<strong class="output-item-subtitle"
                        >${item.subTitle}:</strong
                      >
                      ${item.content}`
                  : html`${item.content}`}
              </li>
            `,
          )}
        </ul>
      </div>
    `;
  }

  _renderRecommendations(result) {
    const overallAssessment =
      result.summary?.overall_assessment || result.overall_assessment;
    const recs =
      overallAssessment?.corrective_actions_needed &&
      overallAssessment.corrective_actions_needed.length > 0
        ? overallAssessment.corrective_actions_needed
        : null;

    return html`<div class="audit-recommendations">
      <div class="audit-recommendations-title">Recommendations</div>
      ${recs
        ? html`
            <ul class="audit-recommendations-list">
              ${recs.map(action => html`<li>${action}</li>`)}
            </ul>
          `
        : html`<ul class="audit-recommendations-list">
            <li>No recommendations.</li>
          </ul>`}
    </div>`;
  }

  _parseMarkdown(text) {
    // Filter out recommendation sections from the text first
    const filteredText = this._filterRecommendationsFromText(text);

    // Simple markdown parser for common elements
    let htmlContent = filteredText;

    // Convert headers (### ## #)
    htmlContent = htmlContent.replace(/^### (.+)$/gm, '<h3>$1</h3>');
    htmlContent = htmlContent.replace(/^## (.+)$/gm, '<h2>$1</h2>');
    htmlContent = htmlContent.replace(/^# (.+)$/gm, '<h1>$1</h1>');

    // Convert bold text (**text** or __text__)
    htmlContent = htmlContent.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    htmlContent = htmlContent.replace(/__(.+?)__/g, '<strong>$1</strong>');

    // Convert bullet lists
    htmlContent = htmlContent.replace(/^- (.+)$/gm, '<li>$1</li>');

    // Wrap consecutive <li> elements in <ul>
    htmlContent = htmlContent.replace(/(<li>.*<\/li>)/gs, match => {
      const items = match.split('\n').filter(line => line.trim());
      return `<ul>${items.join('\n')}</ul>`;
    });

    // Convert line breaks to paragraphs
    const paragraphs = htmlContent.split('\n\n').filter(p => p.trim());
    const formattedParagraphs = paragraphs.map(p => {
      const trimmed = p.trim();

      if (
        trimmed.startsWith('<h') ||
        trimmed.startsWith('<ul') ||
        trimmed.startsWith('<li')
      ) {
        return trimmed;
      }
      return `<p>${trimmed}</p>`;
    });

    return this._unsafeHTML(formattedParagraphs.join('\n'));
  }

  _filterRecommendationsFromText(text) {
    const lines = text.split('\n');
    const filteredLines = [];

    let skipSection = false;

    lines.forEach(line => {
      const trimmedLine = line.trim();

      // Check if this is a recommendation header
      if (
        trimmedLine.match(/^#+\s.*recommendation/i) ||
        trimmedLine.match(/^\*\*.*recommendation.*:\*\*/i)
      ) {
        skipSection = true;
        return;
      }

      // Check if we're starting a new section (reset skip flag)
      if (trimmedLine.match(/^#+\s/) || trimmedLine.match(/^\*\*.*:\*\*/)) {
        const title = trimmedLine.toLowerCase();
        skipSection =
          title.includes('recommendation') || title.includes('suggest');
      }

      // Add line if we're not skipping this section
      if (!skipSection) {
        filteredLines.push(line);
      }
    });

    return filteredLines.join('\n');
  }

  _unsafeHTML(htmlString) {
    // Create a template element to safely parse HTML
    const template = document.createElement('template');
    template.innerHTML = htmlString;
    return template.content;
  }

  _renderErrorContent() {
    return html`<div>
      <div style="color:red">
        Audit failed: ${this.__auditError?.message || this.__auditError}
      </div>
    </div>`;
  }

  _renderIdleContent() {
    return html`<div>
      <div>Start an audit to see status here.</div>
    </div>`;
  }

  _isRecommendationSection(section) {
    if (!section || !section.title) return false;
    const title = section.title.toLowerCase();
    return title.includes('recommendation') || title.includes('suggest');
  }

  _renderJsonOutput(data) {
    if (!data) return '';

    const sections = [];

    // Render billing code analysis
    if (data.billing_code_analysis) {
      Object.entries(data.billing_code_analysis).forEach(([code, analysis]) => {
        sections.push(this._renderBillingCodeSection(code, analysis));
      });
    }

    // Render overall assessment
    if (data.overall_assessment) {
      sections.push(
        this._renderOverallAssessmentSection(data.overall_assessment),
      );
    }

    return html`${sections}`;
  }

  _renderBillingCodeSection(code, analysis) {
    return html`
      <div class="output-section">
        <div class="output-section-title">Billing Code ${code} Analysis</div>
        ${this._renderCodeRequirements(analysis.code_requirements)}
        ${this._renderDiagnosisSupport(analysis.diagnosis_support)}
        ${this._renderDocumentationSupport(analysis.documentation_support)}
      </div>
    `;
  }

  _renderCodeRequirements(requirements) {
    if (!requirements) return '';

    return html`
      <div class="output-subsection">
        <h3>Code Requirements</h3>
        <ul class="output-section-list">
          <li>
            <strong>Required Regions:</strong> ${requirements.required_regions}
          </li>
          <li>
            <strong>Actual Regions Treated:</strong>
            ${requirements.actual_regions_treated}
          </li>
          <li>
            <strong>Regions with Complaints Treated:</strong>
            ${requirements.regions_with_complaints_treated}
          </li>
          ${requirements.regions_documented?.length
            ? html`
                <li>
                  <strong>Regions Documented:</strong>
                  ${requirements.regions_documented.join(', ')}
                </li>
              `
            : ''}
          ${requirements.billable_regions?.length
            ? html`
                <li>
                  <strong>Billable Regions:</strong>
                  ${requirements.billable_regions.join(', ')}
                </li>
              `
            : ''}
          ${requirements.missing_regions?.length
            ? html`
                <li>
                  <strong>Missing Regions:</strong>
                  ${requirements.missing_regions.join(', ')}
                </li>
              `
            : ''}
        </ul>
      </div>
    `;
  }

  _renderDiagnosisSupport(support) {
    if (!support) return '';

    return html`
      <div class="output-subsection">
        <h3>Diagnosis Support</h3>
        <ul class="output-section-list">
          <li>
            <strong>Has Required Diagnoses:</strong>
            ${support.has_required_diagnoses ? 'Yes' : 'No'}
          </li>
          ${support.diagnosis_codes_present?.length
            ? html`
                <li>
                  <strong>Diagnosis Codes Present:</strong>
                  ${support.diagnosis_codes_present.join(', ')}
                </li>
              `
            : ''}
          <li>
            <strong>Documentation Complete:</strong>
            ${support.diagnosis_documentation_complete ? 'Yes' : 'No'}
          </li>
          ${support.diagnosis_gaps?.length
            ? html`
                <li>
                  <strong>Documentation Gaps:</strong>
                  ${support.diagnosis_gaps.join(', ')}
                </li>
              `
            : ''}
        </ul>
      </div>
    `;
  }

  _renderDocumentationSupport(support) {
    if (!support) return '';

    return html`
      <div class="output-subsection">
        <h3>Documentation Support</h3>
        <ul class="output-section-list">
          <li>
            <strong>Treatment Documented:</strong>
            ${support.treatment_documented ? 'Yes' : 'No'}
          </li>
          <li>
            <strong>Regions Treated Documented:</strong>
            ${support.regions_treated_documented ? 'Yes' : 'No'}
          </li>
          ${support.documentation_deficiencies?.length
            ? html`
                <li>
                  <strong>Documentation Deficiencies:</strong>
                  ${support.documentation_deficiencies.join(', ')}
                </li>
              `
            : ''}
        </ul>
      </div>
    `;
  }

  _renderOverallAssessmentSection(assessment) {
    return html`
      <div class="output-section">
        <div class="output-section-title">Overall Assessment</div>
        <ul class="output-section-list">
          ${assessment.documentation_quality
            ? html`
                <li>
                  <strong>Documentation Quality:</strong>
                  ${assessment.documentation_quality}
                </li>
              `
            : ''}
          ${assessment.billing_accuracy
            ? html`
                <li>
                  <strong>Billing Accuracy:</strong>
                  <span
                    class="${assessment.billing_accuracy === 'Incorrect'
                      ? 'compliant-no'
                      : 'compliant-yes'}"
                    >${assessment.billing_accuracy.toUpperCase()}</span
                  >
                </li>
              `
            : ''}
          ${assessment.corrective_actions_needed?.length
            ? html`
                <li>
                  <strong>Corrective Actions Needed:</strong>
                  <ul class="nested-list">
                    ${assessment.corrective_actions_needed.map(
                      action => html`<li>${action}</li>`,
                    )}
                  </ul>
                </li>
              `
            : ''}
        </ul>
      </div>
    `;
  }
}

customElements.define('neb-overlay-audit-shield', NebOverlayAuditShield);
